import { z } from 'zod';
import { ApiError } from '@/api/ApiError';
import { ErrorCodes } from 'schemas';
import { UNKNOWN_ERROR_API_ERROR } from './unknownError';
import { captureApiError } from '@/utils/sentry';

/**
 * Standardized error handling for API requests.
 * Re-throws ApiError instances as-is, converts ZodError to ApiError with BAD_REQUEST code,
 * and wraps all other errors in ApiError with INTERNAL_SERVER_ERROR code.
 * Also captures errors in Sentry for monitoring.
 *
 * @param error - The caught error to handle
 * @param method - HTTP method used for the request
 * @param endpoint - API endpoint that was called
 * @param context - Additional context for error tracking
 * @throws {ApiError} - Always throws an ApiError instance
 */
export function handleApiError(
  error: unknown,
  method?: string,
  endpoint?: string,
  context?: Record<string, unknown>
): never {
  if (error instanceof z.ZodError) {
    // Capture validation errors in Sentry
    if (method && endpoint) {
      captureApiError(error, method, endpoint, {
        ...context,
        errorType: 'validation_error',
        fieldErrors: error.issues.map((e) => ({
          field: e.path.join('.'),
          message: e.message,
        })),
      });
    }

    throw new ApiError({
      type: 'error',
      error: 'Invalid response format from server',
      code: ErrorCodes.BAD_REQUEST,
      message: error.message,
    });
  }

  if (error instanceof ApiError) {
    // Capture ApiError instances in Sentry
    if (method && endpoint) {
      captureApiError(error, method, endpoint, {
        ...context,
        errorType: 'api_error',
        errorCode: error.code,
      });
    }

    throw error;
  }

  // Capture unknown errors in Sentry
  if (method && endpoint) {
    captureApiError(
      error instanceof Error ? error : new Error('Unknown error'),
      method,
      endpoint,
      {
        ...context,
        errorType: 'unknown_error',
        originalError: error instanceof Error ? error.message : String(error),
      }
    );
  }

  throw UNKNOWN_ERROR_API_ERROR;
}
